import React, { useState, useEffect, useMemo } from 'react';
import axiosInstance from '../../../axiosInstance';
import { Container, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-phone-number-input/style.css';
import { isValidPhoneNumber } from 'react-phone-number-input';
import CommonModal from '../../common/CommonModal';
import { getApiErrorMessage } from '../../../utils/errorUtils';

import { Formik, Form, Field } from 'formik';
import Button from '../../common/Button';
import Grid from 'apollo-react/components/Grid';
import SwitchInput from '../../common/Form/switchInput';
import { renderInputComponent } from '../../common/Form/renderInputComponent';
import PhoneInput from '../../common/Form/PhoneInput';
import { ROLE_STATUS } from '../../../constants/role.constant';
import { createUserSchema, updateUserSchema } from '../../../validation/user';
import useUnsavedChangesWarning from '../../../hooks/ui/navigation/useUnsavedChangesWarning';
import useRolesApi from '../../../hooks/api/roles/useRolesApi';

const DynamicForm = ({ persona }) => {
  const [formFields, setFormFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [formDataCopy, setFormDataCopy] = useState({});
  const [roles, setRoles] = useState([]);
  const [isUpdate, setIsUpdate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resp, setResp] = useState('');
  const [token, setToken] = useState('');
  const [phone, setPhone] = useState();
  const [error, setError] = useState('');
  const [initialValues, setInitialValues] = useState({});
  const [discardModal, setDiscardModal] = useState(false);

  const [t] = useTranslation();
  const { id } = useParams();
  const { getInternalRoles } = useRolesApi();

  const isDirty = useMemo(() => {
    const keys = new Set([
      ...Object.keys(formData),
      ...Object.keys(formDataCopy),
    ]);
    for (const key of keys) {
      if (String(formData[key] ?? '') !== String(formDataCopy[key] ?? ''))
        return true;
    }
    return false;
  }, [formData, formDataCopy]);

  const { navigateBack } = useUnsavedChangesWarning(isDirty);

  // const basePath = persona === "super-admin" ? "/super-admin" : "/admin";

  // Fetch form fields and data (if updating) from the backend
  useEffect(() => {
    const fetchFormMetadata = async () => {
      try {
        const response = await axiosInstance
          // eslint-disable-next-line no-undef
          .get(`${process.env.REACT_APP_SERVER_URL}/api/users/form-fields`, {
            withCredentials: true,
          })
          .catch((err) => console.log(err));

        setFormFields(response.data);

        const rolesPayload = await getInternalRoles().catch((err) => {
          console.log(err);
          return [];
        });
        setRoles(
          rolesPayload.filter(
            (role) => role.status === ROLE_STATUS.PUBLISHED_ACTIVE
          )
        );

        if (id) {
          // If we have an ID, fetch data for updating
          setIsUpdate(true);

          const { data: recordData } = await axiosInstance
            // eslint-disable-next-line no-undef
            .get(`${process.env.REACT_APP_SERVER_URL}/api/users/${id}`, {
              withCredentials: true,
            })
            .catch((err) => console.log(err));

          const phone = recordData.phone;
          setPhone(phone);
          setFormData(recordData);
          setFormDataCopy(recordData);
        }

        setLoading(false);
      } catch (err) {
        setResp(t('Error fetching form metadata or data'));
        console.error(t('Error fetching form metadata or data: '), err);
        setLoading(false);
      }
    };

    fetchFormMetadata();
  }, [id, t, axiosInstance]);

  useEffect(() => {
    const initialValues = formFields.reduce((acc, field) => {
      acc[field.name] = formData[field.name] || '';
      return acc;
    }, {});

    setInitialValues(initialValues);
  }, [formFields, formData]);

  const handleChange = (name, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // // Handle input changes
  // const handleInputChange = (event) => {
  //   const { name, value, type, checked } = event.target;
  //   const s = type === "checkbox" ? (checked ? 1 : 0) : value;
  //   setFormData({
  //     ...formData,
  //     [name]: type === "checkbox" ? s : value,
  //   });
  // };

  const validate = () => {
    const schema = isUpdate ? updateUserSchema(t) : createUserSchema(t);
    const result = schema.safeParse(formData);

    if (!result.success) {
      const firstError = result.error.issues[0].message;
      toast.error(firstError, { position: 'top-center' });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    try {
      const isValid = validate();
      if (!isValid) return false;

      if (isUpdate) {
        if (!formData.password) delete formData.password;
        await axiosInstance.put(`/api/users/${id}`, formData);
        toast.success('User Updated Successfully!', {
          position: 'top-center',
        });
        navigateBack();
      } else {
        const resp = await axiosInstance.post('/api/users/', formData);
        toast.success(t('User Added Successfully!'), {
          position: 'top-center',
        });
        setToken('');
        if (resp.data.token) {
          setToken(resp.data.token);
        } else {
          navigateBack();
        }
      }
    } catch (err) {
      const msg = getApiErrorMessage(err, t('Error submitting form'));
      toast.error(msg, { position: 'top-center' });
      setResp(msg);
      return false;
    }
  };

  if (loading) return <div>Loading...</div>;

  const handlePhoneChange = (phone) => {
    setPhone(phone);
    setFormData({
      ...formData,
      phone: phone,
    });
    if (phone && !isValidPhoneNumber(phone)) {
      setError('Invalid phone number');
    } else {
      setError('');
    }
  };

  return (
    <div
      className="content"
      style={{
        border: 'solid 1px #E9E9E9',
        padding: '24px',
        borderRadius: '5px',
      }}
    >
      {resp && <p>{resp}</p>}
      <Grid container justifyContent={'center'} sx={{ width: '100%' }}>
        <Grid size={12}>
          <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            onSubmit={handleSubmit}
          >
            {({ handleSubmit, setFieldValue }) => (
              <Form onSubmit={handleSubmit}>
                <Grid container spacing={2} sx={{ width: '100%' }}>
                  {formFields.map((field, index) => (
                    <React.Fragment key={field.name}>
                      {/* Start a new row for every two inputs */}
                      {index % 2 === 0 && (
                        <Grid
                          container
                          spacing={2}
                          sx={{ width: '100%' }}
                        ></Grid>
                      )}
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Field key={field.name} name={field.name}>
                          {({ field: formikField, meta }) => {
                            const handleFieldChange = (value) => {
                              console.log('formikField', formikField, value);
                              setFieldValue(formikField.name, value);
                              handleChange(formikField.name, value);
                            };
                            if (field.type === 'dropdown') {
                              field.options = roles.map((role) => ({
                                key: role.id,
                                label: role.name,
                              }));
                            }
                            return renderInputComponent(
                              field,
                              formData,
                              formikField,
                              meta,
                              handleFieldChange
                            );
                          }}
                        </Field>
                      </Grid>
                    </React.Fragment>
                  ))}
                </Grid>
                {/* {isUpdate ? (
									<Form.Group
										className="mb-3"
										controlId="exampleForm.ControlInput3"
									>
										<Form.Check
											name={"blocked"}
											onChange={handleInputChange}
											type="switch"
											label={t("Blocked")}
											checked={formData["blocked"]}
											value={formData["blocked"]}
										/>
									</Form.Group>
								) : null} */}
                {isUpdate ? (
                  <>
                    <div className="mb-2">
                      <Link
                        to={`/${persona}/user/privileges/${id}`}
                        className="text-decoration-none"
                      >
                        Set Specific Privileges
                      </Link>
                    </div>
                    <Field name={'active'}>
                      {({ field: formikField, meta }) => (
                        <SwitchInput
                          {...formikField}
                          meta={meta}
                          name={'active'}
                          onChange={(checked) =>
                            handleChange('active', checked)
                          }
                          label={'Active'}
                          checked={formData['active']}
                          value={formData['active']}
                          style={{
                            marginTop: '16px',
                            marginBottom: '8px',
                          }}
                        />
                      )}
                    </Field>
                  </>
                ) : null}
                <Field key="phone" name="phone">
                  {({ field: formikField, meta }) => (
                    <PhoneInput
                      {...formikField}
                      meta={meta}
                      label={t('Phone Number')}
                      value={formData['phone'] || '+20'}
                      onChange={(value) => handleChange('phone', value)}
                      error={error}
                    />
                  )}
                </Field>

                {/* {error && <div style={{ color: "red" }}>{error}</div>} */}

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: '50px',
                  }}
                >
                  <Button
                    color="primary"
                    onClick={() => setDiscardModal(true)}
                    style={{
                      padding: '12px 22px',
                      width: '157px',
                      height: '40px',
                      marginRight: '24px',
                    }}
                    text={t('Discard')}
                  />

                  <Button
                    color="primary"
                    type="submit"
                    style={{
                      padding: '12px 22px',
                      width: '157px',
                      height: '40px',
                    }}
                    text={t('Save')}
                  />
                </div>
              </Form>
            )}
          </Formik>{' '}
          {token && (
            <div className="mt-2 p-2 card">
              <h6>Generated Token:</h6>
              <p className="">{token}</p>
            </div>
          )}
        </Grid>
      </Grid>

      <CommonModal
        open={discardModal}
        message="Are you sure you want to discard your changes?"
        buttonProps={[
          {
            onClick: () => {
              setDiscardModal(false);
            },
          },
          {
            label: 'Confirm',
            onClick: () => {
              setFormData(formDataCopy);
              setDiscardModal(false);
            },
          },
        ]}
        hideHeader
        id="hideHeader"
      />
    </div>
  );
};

export default DynamicForm;
